import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import AdminLayout from "@/components/admin/AdminLayout";
import axios from "axios";
import { Send, User, Bot, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminMessagesPage = () => {
  const [cases, setCases] = useState([]);
  const [selectedCase, setSelectedCase] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchCases();
  }, []);

  useEffect(() => {
    if (selectedCase) {
      fetchMessages(selectedCase.id);
      markMessagesRead(selectedCase.id);
    }
  }, [selectedCase]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const fetchCases = async () => {
    const token = localStorage.getItem("admin_token");
    try {
      const response = await axios.get(`${API}/admin/cases`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCases(response.data);
    } catch (error) {
      console.error("Cases error:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (caseId) => {
    const token = localStorage.getItem("admin_token");
    try {
      const response = await axios.get(`${API}/admin/cases/${caseId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(response.data.messages.reverse());
    } catch (error) {
      console.error("Messages error:", error);
    }
  };

  const markMessagesRead = async (caseId) => {
    const token = localStorage.getItem("admin_token");
    try {
      await axios.put(`${API}/admin/messages/${caseId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchCases(); // Refresh unread counts
    } catch (error) {
      console.error("Mark read error:", error);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedCase) return;
    
    setSending(true);
    const token = localStorage.getItem("admin_token");
    try {
      await axios.post(`${API}/admin/messages`, 
        { case_id: selectedCase.id, content: newMessage },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setNewMessage("");
      fetchMessages(selectedCase.id);
      toast.success("Message sent");
    } catch (error) {
      console.error("Send error:", error);
      toast.error("Failed to send message");
    } finally {
      setSending(false);
    }
  };

  const formatTime = (dateStr) => {
    return new Date(dateStr).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  };

  const filteredCases = cases.filter(c => {
    const userName = c.user?.full_name || '';
    const userEmail = c.user?.email || '';
    return userName.toLowerCase().includes(searchTerm.toLowerCase()) ||
           userEmail.toLowerCase().includes(searchTerm.toLowerCase());
  });

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-[#10B981] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="h-[calc(100vh-8rem)]" data-testid="admin-messages">
        <div className="grid lg:grid-cols-3 gap-6 h-full">
          {/* Cases List */}
          <div className="bg-slate-800 rounded-2xl border border-slate-700 overflow-hidden flex flex-col">
            <div className="p-4 border-b border-slate-700">
              <h2 className="text-lg font-semibold text-white mb-3">Conversations</h2>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  placeholder="Search clients..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 bg-slate-700 border-slate-600 text-white placeholder:text-slate-500"
                />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto">
              {filteredCases.map((c) => (
                <button
                  key={c.id}
                  onClick={() => setSelectedCase(c)}
                  className={`w-full p-4 text-left border-b border-slate-700 hover:bg-slate-700/50 transition-colors ${
                    selectedCase?.id === c.id ? 'bg-slate-700' : ''
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#10B981] rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-semibold">
                        {c.user?.full_name?.charAt(0) || "?"}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <p className="text-white font-medium truncate">{c.user?.full_name}</p>
                        {c.unread_messages > 0 && (
                          <span className="ml-2 px-2 py-0.5 bg-[#10B981] text-white text-xs font-bold rounded-full">
                            {c.unread_messages}
                          </span>
                        )}
                      </div>
                      <p className="text-slate-400 text-sm truncate">{c.user?.email}</p>
                    </div>
                  </div>
                </button>
              ))}
              {filteredCases.length === 0 && (
                <div className="p-8 text-center text-slate-400">
                  No conversations found
                </div>
              )}
            </div>
          </div>

          {/* Chat Area */}
          <div className="lg:col-span-2 bg-slate-800 rounded-2xl border border-slate-700 overflow-hidden flex flex-col">
            {selectedCase ? (
              <>
                {/* Chat Header */}
                <div className="p-4 border-b border-slate-700 flex items-center gap-4">
                  <div className="w-10 h-10 bg-[#10B981] rounded-full flex items-center justify-center">
                    <span className="text-white font-semibold">
                      {selectedCase.user?.full_name?.charAt(0) || "?"}
                    </span>
                  </div>
                  <div>
                    <p className="text-white font-semibold">{selectedCase.user?.full_name}</p>
                    <p className="text-slate-400 text-sm">{selectedCase.user?.email}</p>
                  </div>
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                  {messages.map((msg) => {
                    const isAdvisor = msg.sender_type === "advisor";
                    return (
                      <div
                        key={msg.id}
                        className={`flex ${isAdvisor ? 'justify-end' : 'justify-start'}`}
                      >
                        <div className={`flex items-end gap-2 max-w-[70%] ${isAdvisor ? 'flex-row-reverse' : ''}`}>
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                            isAdvisor ? 'bg-purple-600' : 'bg-[#10B981]'
                          }`}>
                            {isAdvisor ? (
                              <Bot className="w-4 h-4 text-white" />
                            ) : (
                              <User className="w-4 h-4 text-white" />
                            )}
                          </div>
                          <div className={`rounded-2xl px-4 py-3 ${
                            isAdvisor 
                              ? 'bg-purple-600 text-white rounded-br-md' 
                              : 'bg-slate-700 text-white rounded-bl-md'
                          }`}>
                            <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                            <p className={`text-xs mt-1 ${isAdvisor ? 'text-purple-200' : 'text-slate-400'}`}>
                              {formatDate(msg.created_at)} {formatTime(msg.created_at)}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {messages.length === 0 && (
                    <div className="text-center py-12 text-slate-400">
                      No messages yet. Start the conversation!
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {/* Input */}
                <div className="p-4 border-t border-slate-700">
                  <form onSubmit={handleSend} className="flex gap-3">
                    <Input
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Type your message..."
                      className="flex-1 bg-slate-700 border-slate-600 text-white placeholder:text-slate-500"
                    />
                    <Button
                      type="submit"
                      disabled={sending || !newMessage.trim()}
                      className="bg-[#10B981] hover:bg-[#059669]"
                    >
                      {sending ? (
                        <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                      ) : (
                        <Send className="w-5 h-5" />
                      )}
                    </Button>
                  </form>
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-slate-400">
                Select a conversation to start messaging
              </div>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminMessagesPage;
